import classNames from "classnames";
import { PRIO, STATUS } from "../../lib/design";
import { formatDay, isOverdue } from "../../lib/date";
import { Avatar } from "../Avatar";
import { QuickAdd } from "../QuickAdd";
import type { LayoutProps } from "./ListLayout";

/** Layout Galerie — grid de cartes avec cover coloré (couleur du groupe,
 * sinon couleur du statut). Composer en carte « + » par groupe. */
export function GalleryLayout({
  groups,
  selectedTaskId,
  onSelect,
  hiddenProps,
  composerFor,
  composerBusy,
}: Omit<LayoutProps, "onToggleDone" | "canDrop" | "onDropTask">) {
  const show = (p: string) => !hiddenProps?.includes(p);

  return (
    <>
      {groups.map((g) => {
        const onAdd = composerFor(g.key);
        return (
          <section key={g.key}>
            <div
              className="group-title"
              style={g.color ? { color: g.color } : undefined}
            >
              {g.color && (
                <span className="group-dot" style={{ background: g.color }} />
              )}
              {g.label}
              <span style={{ fontWeight: 500 }}>{g.items.length}</span>
            </div>
            <div className="gallery-grid">
              {g.items.map((t) => {
                const done = t.status === "done";
                const cover = g.color ?? STATUS[t.status].dot;
                return (
                  <div
                    key={t.id}
                    className={classNames("gallery-card", {
                      selected: t.id === selectedTaskId,
                    })}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      onSelect(t.id === selectedTaskId ? null : t.id)
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      onSelect(t.id === selectedTaskId ? null : t.id)
                    }
                  >
                    <div
                      className="gallery-cover"
                      style={{
                        background: `color-mix(in srgb, ${cover} 16%, var(--bg))`,
                      }}
                    >
                      <span
                        className="group-dot"
                        style={{ background: cover }}
                      />
                    </div>
                    <div className="gallery-body">
                      <div
                        className={classNames("task-title", { done })}
                        style={{ fontSize: 12.5 }}
                      >
                        {t.replicated && (
                          <span
                            style={{ color: "var(--violet)" }}
                            title="Work item PM"
                          >
                            ◆{" "}
                          </span>
                        )}
                        {t.title}
                      </div>
                      <div className="board-card-meta">
                        {show("status") && (
                          <span className="task-meta-status">
                            {STATUS[t.status].label}
                          </span>
                        )}
                        {show("priority") && (
                          <span
                            className="chip"
                            style={{
                              color: PRIO[t.priority].color,
                              background: PRIO[t.priority].bg,
                            }}
                          >
                            {PRIO[t.priority].label}
                          </span>
                        )}
                        {show("due") && t.dueKey && (
                          <span
                            className={classNames("task-due", {
                              overdue: !done && isOverdue(t.dueKey),
                            })}
                          >
                            ◷ {formatDay(t.dueKey)}
                          </span>
                        )}
                        {show("assignees") &&
                          t.assignees &&
                          t.assignees.length > 0 && (
                            <span
                              className="avatar-stack"
                              style={{ marginLeft: "auto" }}
                            >
                              {t.assignees.slice(0, 3).map((a) => (
                                <Avatar key={a.ref} name={a.name} size={18} />
                              ))}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {onAdd && (
                <div className="gallery-card gallery-composer">
                  <QuickAdd
                    placeholder="Nouvelle tâche…"
                    onAdd={onAdd}
                    busy={composerBusy}
                  />
                </div>
              )}
            </div>
          </section>
        );
      })}
    </>
  );
}
